import { orderBy } from "lodash";

import {
  formatValidQueryParamUrlCommaSeparated,
  getFullName
} from "utils/common";
import { formatPatientStaffTask } from "utils/patients.util";

import { emptySplitApi } from "./empty-split-api";
import type { CSVReportResponse } from "./types/reports.type";
import {
  type CreateBulkTaskInput,
  StaffTask,
  StaffTaskAttachment,
  type StaffTaskBulkReassignInput,
  type StaffTaskCompletionAudit,
  type StaffTaskHistoryEvent,
  StaffTaskInput,
  type StaffTasksDownloadQuery,
  StaffTasksFilter,
  type StaffTasksGroupByPriorityItem,
  type StaffTasksGroupByPriorityResponse,
  StaffTasksPaginationResponse,
  StaffTasksResponse,
  TaskCommentResponse,
  UpdateBulkTaskRequest
} from "./types/staff-tasks.type";

// `mentions` (description @mentions) and the comments' `mentioned_users` are opt-in on the backend
// and an unknown RestQL key 400s an older one, so they are named only with TEAM_MENTIONS_FEATURE on.
// `team` / `team_claimed_at` (TEAMS_FEATURE) are likewise named only with that flag on.
export const getStaffTaskQuery = (
  includeMentions = false,
  includeTeam = false
) =>
  `{id,title,priority,status,description,due_at,completed_at,updated_at,created_at,implementation,implementation_phase,is_training,patient{id,first_name,middle_name,last_name,title,suffix,preferred_name,photo},comments{id,text,user{id,full_name},created_at,created_by${includeMentions ? ",mentioned_users" : ""}},assignees{user{id,first_name,last_name,full_name,suffix,title,is_active,photo}},assignee{id,first_name,last_name,full_name,suffix,title,is_active,photo},created_by,assigned_tags,attachments{id,file,original_filename,created_at,created_by,updated_by,can_delete},color${includeMentions ? ",mentions" : ""}${includeTeam ? ",team{id,name,color,is_active,alert_mode},team_claimed_at" : ""}}`;

const DEFAULT_STAFF_TASKS_QUERY = getStaffTaskQuery();

/** The list projection with the team pill (TEAMS_FEATURE): Home → For me and the team filter. */
export const STAFF_TASKS_WITH_TEAM_QUERY = getStaffTaskQuery(false, true);

/** A task id, or the id plus whether to read its @mentions (TEAM_MENTIONS_FEATURE) / team (TEAMS_FEATURE). */
export type GetStaffTaskArg =
  | number
  | { id: number; includeMentions?: boolean; includeTeam?: boolean };

export const GROUP_BY_PRIORITY_QUERY =
  "{id,title,priority,status,description,due_at,completed_at,updated_at,created_at,implementation,implementation_phase," +
  "patient{id,first_name,middle_name,last_name,title,suffix,preferred_name}," +
  "assignees{user{id,first_name,last_name,full_name,suffix,title}}," +
  "assignee{id,first_name,last_name,full_name,suffix,title}," +
  "created_by,comment_count,assigned_tags,color}";

const STAFF_TASKS_CSV_QUERY =
  "{created_at,created_by,title,description,priority,patient_name,patient_id,assignees,assigned_tags,status,due_at,updated_at}";

const DEFAULT_CSV_HEADER =
  "created_at,created_by,title,description,priority,patient_name,patient_id,assignees,assigned_tags,status,due_at,updated_at";

const DEFAULT_CSV_LABELS =
  "Task Created At Date,Task Created By,Task Title,Task Description,Task Priority,Patient Name,Patient Id,Task Assignees,Assigned Tags,Task Status,Task Due Date,Task Last Updated";

export const staffTasksApi = emptySplitApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getStaffTasks: builder.query<StaffTasksResponse, StaffTasksFilter>({
      query: ({ limit = 25, offset = 0, ...filters }) => {
        const customFiltersString = formatValidQueryParamUrlCommaSeparated({
          ...filters,
          limit,
          offset,
          query: filters.query || DEFAULT_STAFF_TASKS_QUERY,
          ordering:
            filters.ordering ??
            (filters.is_order_by_due_at ? "due_at" : "-updated_at")
        });

        return `/staff-tasks/?${customFiltersString}`;
      },
      providesTags: ["StaffTasks"],
      transformResponse: (response: StaffTasksPaginationResponse, _, arg) => {
        const formattedTasks = response?.results?.map((task) => {
          const formattedTask = formatPatientStaffTask(task);

          return {
            ...formattedTask,
            patient: formattedTask.patient
              ? {
                  ...formattedTask.patient,
                  fullName: getFullName({
                    firstName: formattedTask.patient.first_name,
                    middleName: formattedTask.patient.middle_name || "",
                    preferredName: formattedTask.patient.preferred_name || "",
                    lastName: formattedTask.patient.last_name,
                    title: formattedTask.patient.title,
                    suffix: formattedTask.patient.suffix
                  })
                }
              : formattedTask.patient
          };
        });

        if (arg.ordering !== undefined) {
          return formattedTasks;
        }
        return orderBy(
          formattedTasks,
          [arg.is_order_by_due_at ? "due_at" : "updated_at"],
          [arg.is_order_by_due_at ? "asc" : "desc"]
        );
      }
    }),
    getPatientStaffTasks: builder.query<
      { count: number; tasks: StaffTasksResponse },
      { patient: number; assignee?: number }
    >({
      query: ({ patient, assignee }) => {
        const customFiltersString = formatValidQueryParamUrlCommaSeparated({
          patient,
          assignee,
          is_active: true,
          is_complete: false,
          limit: 3,
          offset: 0,
          query: DEFAULT_STAFF_TASKS_QUERY,
          ordering: "-created_at"
        });

        return `/staff-tasks/?${customFiltersString}`;
      },
      providesTags: ["StaffTasks"],
      transformResponse: (response: StaffTasksPaginationResponse, _, arg) => {
        const formattedTasks = response?.results?.map((task) => {
          const formattedTask = formatPatientStaffTask(task);

          return {
            ...formattedTask,
            patient: formattedTask.patient
              ? {
                  ...formattedTask.patient,
                  fullName: getFullName({
                    firstName: formattedTask.patient.first_name,
                    middleName: formattedTask.patient.middle_name || "",
                    preferredName: formattedTask.patient.preferred_name || "",
                    lastName: formattedTask.patient.last_name,
                    title: formattedTask.patient.title,
                    suffix: formattedTask.patient.suffix
                  })
                }
              : formattedTask.patient
          };
        });
        return {
          count: response?.count || 0,
          tasks: formattedTasks
        };
      }
    }),
    queryStaffTasksCounter: builder.query<
      number,
      Omit<StaffTasksFilter, "query" | "limit" | "offset">
    >({
      query: (filters) => {
        const customFiltersString = formatValidQueryParamUrlCommaSeparated({
          ...filters
        });

        return `/staff-tasks/count/?${customFiltersString}`;
      },
      providesTags: ["StaffTasksCount"]
    }),
    getStaffTask: builder.query<StaffTask, GetStaffTaskArg>({
      query: (arg) => {
        const { id, includeMentions, includeTeam } =
          typeof arg === "number"
            ? { id: arg, includeMentions: false, includeTeam: false }
            : arg;
        return `/staff-tasks/${id}/?query=${getStaffTaskQuery(includeMentions, includeTeam)}`;
      },
      providesTags: ["StaffTasks"],
      transformResponse: (response: StaffTask) => {
        const formattedTask = formatPatientStaffTask(response);

        return {
          ...formattedTask,
          patient: formattedTask.patient
            ? {
                ...formattedTask.patient,
                fullName: getFullName({
                  firstName: formattedTask.patient.first_name,
                  middleName: formattedTask.patient.middle_name || "",
                  preferredName: formattedTask.patient.preferred_name || "",
                  lastName: formattedTask.patient.last_name,
                  title: formattedTask.patient.title,
                  suffix: formattedTask.patient.suffix
                })
              }
            : formattedTask.patient
        };
      }
    }),
    getStaffTaskCompletionAudit: builder.query<
      StaffTaskCompletionAudit,
      number
    >({
      query: (id) => `/staff-tasks/${id}/completion-audit/`,
      providesTags: ["StaffTasks"]
    }),
    // Fetched lazily on first expand of the History accordion — never as part
    // of the task projection (see StaffTaskHistory). The StaffTasks tag keeps
    // an open panel fresh after an edit.
    getStaffTaskHistory: builder.query<StaffTaskHistoryEvent[], number>({
      query: (id) => `/staff-tasks/${id}/history/`,
      providesTags: ["StaffTasks"]
    }),

    getStaffTasksGroupByPriority: builder.query<
      StaffTasksGroupByPriorityResponse,
      StaffTasksFilter
    >({
      query: (filters) => {
        const customFiltersString = formatValidQueryParamUrlCommaSeparated({
          ...filters,
          query: filters.query || GROUP_BY_PRIORITY_QUERY,
          ordering: filters.ordering || "due_at,-created_at"
        });

        return `/staff-tasks/?group_by_priority=true&${customFiltersString}`;
      },
      providesTags: ["StaffTasks"],
      transformResponse: (response: StaffTasksGroupByPriorityItem[]) => {
        const result: StaffTasksGroupByPriorityResponse = {
          totalCount: 0,
          items: {}
        };

        response.forEach((item) => {
          const priority = item.priority;
          result.totalCount += item.count;

          const tasks = item.tasks.map((task) => {
            const formattedTask = formatPatientStaffTask(task);
            return {
              ...formattedTask,
              patient: formattedTask.patient
                ? {
                    ...formattedTask.patient,
                    fullName: getFullName({
                      firstName: formattedTask.patient.first_name,
                      middleName: formattedTask.patient.middle_name || "",
                      preferredName: formattedTask.patient.preferred_name || "",
                      lastName: formattedTask.patient.last_name,
                      title: formattedTask.patient.title,
                      suffix: formattedTask.patient.suffix
                    })
                  }
                : formattedTask.patient
            };
          });
          result.items[priority] = {
            priority: item.priority,
            count: item.count,
            tasks
          };
        });

        return result;
      }
    }),

    createStaffTask: builder.mutation<StaffTask, StaffTaskInput>({
      query: (input) => ({
        url: `/staff-tasks/`,
        method: "POST",
        body: input
      }),
      invalidatesTags: ["StaffTasks", "StaffTasksCount"]
    }),
    updateStaffTask: builder.mutation<StaffTask, StaffTaskInput>({
      query: ({ id, ...body }) => ({
        url: `/staff-tasks/${id}/`,
        method: "PATCH",
        body
      }),
      invalidatesTags: ["StaffTasks", "StaffTasksCount"]
    }),
    deleteStaffTask: builder.mutation<StaffTask, number>({
      query: (id) => ({
        url: `/staff-tasks/${id}/`,
        method: "DELETE"
      }),
      invalidatesTags: ["StaffTasks", "StaffTasksCount"]
    }),
    createStaffTaskComment: builder.mutation<
      TaskCommentResponse,
      {
        taskId: number;
        text: string;
        user: number;
        /** Write-only; send ONLY with TEAM_MENTIONS_FEATURE on (an older backend 400s it). */
        mentioned_user_ids?: number[];
      }
    >({
      query: ({ taskId, text, user, mentioned_user_ids }) => ({
        url: `/staff-tasks/${taskId}/comments/`,
        method: "POST",
        body: {
          text,
          user,
          ...(mentioned_user_ids ? { mentioned_user_ids } : {})
        }
      }),
      invalidatesTags: ["StaffTasks"]
    }),
    updateStaffTaskComment: builder.mutation<
      TaskCommentResponse,
      { taskId: number; commentId: number; text: string }
    >({
      query: ({ commentId, taskId, text }) => ({
        url: `/staff-tasks/${taskId}/comments/${commentId}/`,
        method: "PATCH",
        body: { text }
      }),
      invalidatesTags: ["StaffTasks"]
    }),
    deleteStaffTaskComment: builder.mutation<
      void,
      { taskId: number; commentId: number }
    >({
      query: ({ commentId, taskId }) => ({
        url: `/staff-tasks/${taskId}/comments/${commentId}/`,
        method: "DELETE"
      }),
      invalidatesTags: ["StaffTasks"]
    }),
    // The caller's unseen @mentions on this task (description and comments) → seen, and their
    // bell notifications read. TEAM_MENTIONS_FEATURE.
    markStaffTaskMentionsSeen: builder.mutation<{ updated: number }, number>({
      query: (id) => ({
        url: `/staff-tasks/${id}/mark-mentions-seen/`,
        method: "POST"
      }),
      invalidatesTags: (result) =>
        result?.updated
          ? ["StaffTasks", "Notifications", "NotificationUnreadCounts"]
          : []
    }),
    // Teams (TEAMS_FEATURE): the caller takes an unclaimed team task / hands a taken one back.
    // Both answer with the task; the backend locks the row so two members clicking at once get one
    // owner and one "already taken" (400).
    takeStaffTask: builder.mutation<StaffTask, number>({
      query: (id) => ({ url: `/staff-tasks/${id}/take/`, method: "POST" }),
      invalidatesTags: [
        "StaffTasks",
        "StaffTasksCount",
        "Notifications",
        "NotificationUnreadCounts"
      ]
    }),
    releaseStaffTask: builder.mutation<StaffTask, number>({
      query: (id) => ({ url: `/staff-tasks/${id}/release/`, method: "POST" }),
      invalidatesTags: ["StaffTasks", "StaffTasksCount"]
    }),
    downloadStaffTaskCSV: builder.query<
      CSVReportResponse,
      StaffTasksDownloadQuery
    >({
      query: ({
        title = "tasks",
        csv_header = DEFAULT_CSV_HEADER,
        csv_labels = DEFAULT_CSV_LABELS,
        query = STAFF_TASKS_CSV_QUERY,
        ...filters
      }) => {
        const customFiltersString = formatValidQueryParamUrlCommaSeparated({
          ...filters,
          query,
          title,
          csv_header,
          csv_labels,
          ordering:
            filters.ordering ??
            (filters.is_order_by_due_at ? "due_at" : "-updated_at")
        });

        return {
          url: `/staff-tasks/?${customFiltersString}`,
          headers: { Accept: "custom-csv/json" }
        };
      }
    }),
    bulkReassignStaffTasks: builder.mutation<
      { reassigned_count: number },
      StaffTaskBulkReassignInput
    >({
      query: (body) => ({
        url: `/staff-tasks/bulk-reassign/`,
        method: "POST",
        body
      }),
      invalidatesTags: ["StaffTasks", "StaffTasksCount"]
    }),
    bulkCreateTasks: builder.mutation<void, CreateBulkTaskInput>({
      query: (data) => ({
        url: `/staff-tasks/bulk/`,
        method: "POST",
        body: data
      }),
      invalidatesTags: ["StaffTasks", "StaffTasksCount"]
    }),
    bulkUpdateTasks: builder.mutation<
      { updated_count: number; task_ids: number[] },
      UpdateBulkTaskRequest
    >({
      query: (data) => ({
        url: `/staff-tasks/bulk-update/`,
        method: "PATCH",
        body: data
      }),
      invalidatesTags: ["StaffTasks", "StaffTasksCount"]
    }),
    getStaffTaskAttachments: builder.query<StaffTaskAttachment[], number>({
      query: (taskId) => `/staff-tasks/${taskId}/attachments/`,
      providesTags: ["StaffTaskAttachments"]
    }),
    uploadStaffTaskAttachment: builder.mutation<
      StaffTaskAttachment,
      { taskId: number; file: File }
    >({
      query: ({ taskId, file }) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: `/staff-tasks/${taskId}/attachments/`,
          method: "POST",
          body: formData
        };
      },
      invalidatesTags: ["StaffTasks", "StaffTaskAttachments"]
    }),
    deleteStaffTaskAttachment: builder.mutation<
      void,
      { taskId: number; attachmentId: number }
    >({
      query: ({ taskId, attachmentId }) => ({
        url: `/staff-tasks/${taskId}/attachments/${attachmentId}/`,
        method: "DELETE"
      }),
      invalidatesTags: ["StaffTasks", "StaffTaskAttachments"]
    })
  })
});

export const {
  useGetStaffTasksQuery,
  useGetPatientStaffTasksQuery,
  useQueryStaffTasksCounterQuery,
  useCreateStaffTaskMutation,
  useUpdateStaffTaskMutation,
  useDeleteStaffTaskMutation,
  useGetStaffTaskQuery,
  useTakeStaffTaskMutation,
  useReleaseStaffTaskMutation,
  useLazyGetStaffTaskCompletionAuditQuery,
  useLazyGetStaffTaskHistoryQuery,
  useGetStaffTasksGroupByPriorityQuery,
  useCreateStaffTaskCommentMutation,
  useDeleteStaffTaskCommentMutation,
  useUpdateStaffTaskCommentMutation,
  useMarkStaffTaskMentionsSeenMutation,
  useLazyGetStaffTasksQuery,
  useLazyDownloadStaffTaskCSVQuery,
  useBulkCreateTasksMutation,
  useBulkUpdateTasksMutation,
  useBulkReassignStaffTasksMutation,
  useGetStaffTaskAttachmentsQuery,
  useUploadStaffTaskAttachmentMutation,
  useDeleteStaffTaskAttachmentMutation
} = staffTasksApi;
